import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { SqlBuilderButton } from '@twasta/page-runtime/designerKit'
import {
  ArrowLeft, Database, Loader2, Save, Plus, Trash2, Sparkles, X,
  Table2, MessageSquare, Shield, Eye, Download, FileInput, Bot, Star,
  ChevronDown, ChevronRight,
} from 'lucide-react'
import type { EmbeddedDesignerProps } from './embeddedDesigner'
import {
  loadBusinessArea, saveBusinessArea, generateBusinessArea,
  getBusinessAreaSqlContext,
  listVisualSchemas, loadVisualSchema, saveVisualSchema,
  listApplications, saveApplication,
  type VisualSchemaSummary, type ProjectApplication,
} from '@/api/designers'
import SchemaDesignerPage from './SchemaDesignerPage'
import ConnectionPicker from '@/components/connections/ConnectionPicker'
import {
  VariableInsert, RoleAdder, useSqlVariableTokens, useFunctionalRoles,
  type VariableToken,
} from '@/components/common/PolicyPickers'
import { parseSimpleJoin, tableColumns } from './visual/joinHelpers'
import JoinsEditor from './joins/JoinsEditor'
import { fetchTableColumns, listSourceTables, prettify } from './visual/tableSource'
import { useProjectStore } from '@/store/projectStore'
import { useAgentRefresh } from '@/hooks/useAgentRefresh'
import { useDesignHistory } from '@/hooks/useDesignHistory'
import UndoRedoButtons from '@/components/common/UndoRedoButtons'
import { useProjectPermissions } from '@/auth/useProjectPermissions'
import { ReadOnlyBadge } from '@/auth/ReadOnlyBadge'
import { useRunOnlyRedirect } from '@/auth/useRunOnlyRedirect'
import type {
  BusinessArea, SchemaTable, SchemaTableColumn, SchemaJoin,
  SchemaRowSecurityPolicy, SchemaColumnSecurityPolicy, BusinessAreaCreateTarget,
} from './businessarea/types'

const COLTYPE_OPTIONS = ['CHAR', 'NUMBER', 'DATE'] as const
const JOIN_TYPE_OPTIONS = ['INNER', 'LEFT', 'RIGHT', 'FULL'] as const
const FUNCTION_OPTIONS = ['', 'SUM', 'COUNT', 'AVG', 'MIN', 'MAX'] as const
const COL_SEC_ACTIONS = ['HIDE', 'MASK'] as const
type ViewTab = 'tables' | 'joins' | 'security' | 'assistant' | 'preview'

// ── helpers ────────────────────────────────────────────────────────
/** A connection id → the query builder's database key (blank = the project DB). */
function sqlTargetOf(connectionId: string): string | undefined {
  return connectionId ? `conn:${connectionId}` : undefined
}

function blankColumn(): SchemaTableColumn {
  return { DBNAME: '', DISPLAY_NAME: '', COLTYPE: 'CHAR', DBSIZE: 100, DEFAULTFUNCTION: '', DESCRIPTION: '', HIDDEN: false }
}
function blankRowSec(n: number): SchemaRowSecurityPolicy {
  return { POLICY_ID: `RLS_${String(n).padStart(3, '0')}`, TABLE_NAME: '', CONDITION: '', APPLIES_TO_ROLES: [] }
}
function blankColSec(n: number): SchemaColumnSecurityPolicy {
  return { POLICY_ID: `CLS_${String(n).padStart(3, '0')}`, TABLE_NAME: '', COLUMN_NAME: '', ACTION: 'HIDE', APPLIES_TO_ROLES: [] }
}
/** Free text → a file-safe object name (schemas are stored as <name>.json). */
function slugifyName(name: string): string {
  return (name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'new_schema'
}

/** A brand-new, empty Visual Schema file — the same shape the Analytics tab's
    "Create manually" path writes, so both entry points produce one thing. */
function blankSchemaFile(schemaName: string, descr: string, connectionId: string) {
  return {
    SCHEMA: {
      SCHEMA_NAME: schemaName,
      SCHEMA_DESCR: descr || schemaName.replace(/_/g, ' '),
      SCHEMA_TYPE: 'S',
      CONNECTION_ID: connectionId || '',
      MAIN_TABLE: '',
      TABLES: [], JOINS: [], ROW_SECURITY: [], COLUMN_SECURITY: [],
      COLUMN_GROUPS: [],
    },
  }
}

function blankArea(name: string): BusinessArea {
  return {
    AREA_NAME: name, DESCRIPTION: name.replace(/_/g, ' '), AREA_TYPE: 'A', CONNECTION_ID: '',
    SCHEMA_NAME: '', APPLICATION: 'Default', MAIN_TABLE: '', JOINS_DESCRIPTION: '',
    TABLES: [], JOINS: [], ROW_SECURITY: [], COLUMN_SECURITY: [],
    AI_INSTRUCTIONS: '', EXAMPLE_QUESTIONS: [], CREATE_TARGETS: [],
    ACCESS: { ALLOW_ROLES: [], DENY_ROLES: [] }, VISIBILITY: 'published',
  }
}

/** Normalise the DOCUMENTS block the way the backend parser
 *  (`business_area_documents`) does — tolerating a bare list of repository
 *  names and comma-separated doctypes — so loading + saving never loses a
 *  hand-authored block. Returns undefined for "no block", keeping the file
 *  clean for areas that have no documents. */
function sanitizeDocuments(raw: unknown): BusinessArea['DOCUMENTS'] {
  let block = raw as Record<string, unknown>
  if (Array.isArray(raw)) block = { SOURCES: raw }
  if (!block || typeof block !== 'object') return undefined
  const sources = (Array.isArray(block.SOURCES) ? block.SOURCES : [])
    .map((entry: unknown) => {
      const e = (typeof entry === 'string' ? { REPO_OBJ: entry } : entry) as Record<string, unknown>
      if (!e || typeof e !== 'object') return null
      const repo = String(e.REPO_OBJ || e.REPO || e.OBJ_NAME || '').trim().toUpperCase()
      if (!repo) return null
      const rawTypes = e.DOCTYPES ?? e.DOCUMENT_TYPES ?? []
      const doctypes = (Array.isArray(rawTypes) ? rawTypes.map(String)
        : String(rawTypes).split(/[,\s]+/)).map((d) => d.trim().toUpperCase()).filter(Boolean)
      return { REPO_OBJ: repo, DOCTYPES: doctypes, DESCRIPTION: String(e.DESCRIPTION || '') }
    })
    .filter((s): s is NonNullable<typeof s> => s !== null)
  const questions = Array.isArray(block.EXAMPLE_QUESTIONS)
    ? (block.EXAMPLE_QUESTIONS as unknown[]).map(String).filter(Boolean) : []
  const instructions = String(block.AI_INSTRUCTIONS || '')
  if (!sources.length && !questions.length && !instructions) return undefined
  return { SOURCES: sources, AI_INSTRUCTIONS: instructions, EXAMPLE_QUESTIONS: questions }
}

// Hardened loader: defaults every nested field so a malformed file can't crash.
function sanitizeArea(raw: unknown, areaName: string): BusinessArea {
  if (!raw || typeof raw !== 'object') return blankArea(areaName)
  const file = raw as Record<string, unknown>
  const inner = (file.BUSINESS_AREA as Record<string, unknown>) || (file.SCHEMA as Record<string, unknown>) || file
  const access = (inner.ACCESS as Record<string, unknown>) || {}
  const out: BusinessArea = {
    AREA_NAME: areaName,
    DESCRIPTION: String(inner.DESCRIPTION || inner.SCHEMA_DESCR || areaName.replace(/_/g, ' ')),
    AREA_TYPE: 'A',
    CONNECTION_ID: (inner.CONNECTION_ID as string) || '',
    SCHEMA_NAME: (inner.SCHEMA_NAME as string) || '',
    APPLICATION: (inner.APPLICATION as string) || 'Default',
    MAIN_TABLE: (inner.MAIN_TABLE as string) || '',
    JOINS_DESCRIPTION: (inner.JOINS_DESCRIPTION as string) || '',
    JOIN_MODE: inner.JOIN_MODE === 'instruction' ? 'instruction' : 'structured',
    TABLES: Array.isArray(inner.TABLES) ? (inner.TABLES as SchemaTable[]) : [],
    JOINS: Array.isArray(inner.JOINS) ? (inner.JOINS as SchemaJoin[]) : [],
    JOIN_LAYOUT: (inner.JOIN_LAYOUT && typeof inner.JOIN_LAYOUT === 'object')
      ? (inner.JOIN_LAYOUT as BusinessArea['JOIN_LAYOUT']) : undefined,
    ROW_SECURITY: Array.isArray(inner.ROW_SECURITY) ? (inner.ROW_SECURITY as SchemaRowSecurityPolicy[]) : [],
    COLUMN_SECURITY: Array.isArray(inner.COLUMN_SECURITY) ? (inner.COLUMN_SECURITY as SchemaColumnSecurityPolicy[]) : [],
    AI_INSTRUCTIONS: String(inner.AI_INSTRUCTIONS || ''),
    EXAMPLE_QUESTIONS: Array.isArray(inner.EXAMPLE_QUESTIONS) ? (inner.EXAMPLE_QUESTIONS as string[]).map(String) : [],
    CREATE_TARGETS: Array.isArray(inner.CREATE_TARGETS) ? (inner.CREATE_TARGETS as BusinessAreaCreateTarget[]) : [],
    DOCUMENTS: sanitizeDocuments(inner.DOCUMENTS),
    ACCESS: {
      ALLOW_ROLES: Array.isArray(access.ALLOW_ROLES) ? (access.ALLOW_ROLES as string[]) : [],
      DENY_ROLES: Array.isArray(access.DENY_ROLES) ? (access.DENY_ROLES as string[]) : [],
    },
    VISIBILITY: inner.VISIBILITY === 'draft' ? 'draft' : 'published',
  }
  out.TABLES = out.TABLES.map((t) => ({
    TABLE_NAME: t.TABLE_NAME || '', TABLE_DISPLAY_NAME: t.TABLE_DISPLAY_NAME || '', ALIAS: t.ALIAS || '',
    DESCRIPTION: (t as Record<string, unknown>).DESCRIPTION as string || '',
    CONDITION: (t.CONDITION as string) || '',
    COLUMNS: Array.isArray(t.COLUMNS) ? t.COLUMNS.map((c) => ({ ...blankColumn(), ...c })) : [],
  }))
  // Spread first — the insight-db extensions (WHEN_COLUMN_EXISTS /
  // INHERIT_FROM / MASK_TYPE / PATTERN / RANGES / …) must survive the load.
  out.ROW_SECURITY = (out.ROW_SECURITY || []).map((p, i) => ({
    ...p,
    POLICY_ID: p?.POLICY_ID || `RLS_${String(i + 1).padStart(3, '0')}`,
    TABLE_NAME: p?.TABLE_NAME || '', CONDITION: p?.CONDITION || '',
    APPLIES_TO_ROLES: Array.isArray(p?.APPLIES_TO_ROLES) ? p.APPLIES_TO_ROLES : [],
  }))
  out.COLUMN_SECURITY = (out.COLUMN_SECURITY || []).map((p, i) => ({
    ...p,
    POLICY_ID: p?.POLICY_ID || `CLS_${String(i + 1).padStart(3, '0')}`,
    TABLE_NAME: p?.TABLE_NAME || '', COLUMN_NAME: p?.COLUMN_NAME || '', ACTION: p?.ACTION || 'HIDE',
    APPLIES_TO_ROLES: Array.isArray(p?.APPLIES_TO_ROLES) ? p.APPLIES_TO_ROLES : [],
  }))
  return out
}

// ── shared presentation tokens (spec §0.3 mapped onto Twasta CSS vars) ─
const MONO = "var(--font-mono, 'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace)"
const inp: React.CSSProperties = {
  padding: '9px 11px', border: '1px solid var(--color-border)', borderRadius: 8,
  fontSize: 15, background: 'var(--color-input-bg, var(--color-bg))', color: 'var(--color-text)', outline: 'none',
  fontFamily: 'inherit',
}
// Mono (SQL / identifier) input — spec §0.3: monospace at ~12.5px
const monoInp: React.CSSProperties = { ...inp, fontFamily: MONO, fontSize: 14.5 }
// Secondary button (surface-2 + strong border, spec §0.3)
const btn: React.CSSProperties = {
  padding: '8px 14px', border: '1px solid var(--color-border-strong)', borderRadius: 8,
  fontSize: 14, fontWeight: 600, background: 'var(--color-surface-2)',
  color: 'var(--color-text)', cursor: 'pointer', whiteSpace: 'nowrap',
  display: 'inline-flex', alignItems: 'center', gap: 7,
}
// Primary (accent) button
const btnPrimary: React.CSSProperties = {
  ...btn, background: 'var(--color-accent)', color: '#fff', border: '1px solid transparent',
  padding: '8px 17px',
}
// Outlined accent-text button (e.g. “Generate with AI” — spec §6.2.1)
const btnAccentText: React.CSSProperties = {
  ...btn, background: 'transparent', color: 'var(--color-accent)',
  border: '1px solid var(--color-border-strong)',
}
// Quiet icon-only button
const iconBtn: React.CSSProperties = {
  ...btn, padding: 6, background: 'transparent', border: '1px solid transparent',
  color: 'var(--color-text-secondary)',
}
// Danger (delete) icon button
const dangerBtn: React.CSSProperties = {
  ...iconBtn, color: 'var(--color-error)',
}
// Accent link-style button (e.g. “+ Add column”)
const linkBtn: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-accent)',
  fontSize: 13.5, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '6px 2px',
}
// Card / panel — a visible surface step over the --color-bg page (spec §0.3)
const card: React.CSSProperties = {
  background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)',
  borderRadius: 12, overflow: 'hidden',
}
// Inner card strip (table-card header, filter row, grid header) — one step up
const strip: React.CSSProperties = {
  background: 'var(--color-surface-2)', borderBottom: '1px solid var(--color-border)',
}
// Field label wrapper (label above control)
const fieldLabel: React.CSSProperties = {
  display: 'grid', gap: 6, fontSize: 14, fontWeight: 600,
  color: 'var(--color-text-secondary)',
}
// Inline helper note appended to a label / heading (fine print, ≥12px)
const labelNote: React.CSSProperties = {
  fontWeight: 400, fontSize: 14.5, color: 'var(--color-text-faint)',
}
// Section eyebrow / grid-header micro-label (spec §0.3, min 12px for legibility)
const eyebrow: React.CSSProperties = {
  fontSize: 13, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
  color: 'var(--color-text-faint)',
}

export default function BusinessAreaDesignerPage({
  embedded = false, embedProject, embedObjName, onDirtyChange, registerSave, onSaved,
}: EmbeddedDesignerProps = {}) {
  const params = useParams<{ project: string; areaName: string }>()
  const project = embedProject ?? params.project
  useRunOnlyRedirect(project)
  const areaName = embedObjName ?? params.areaName
  const navigate = useNavigate()
  const projectName = project || ''
  const aName = areaName || ''
  const { currentProject, selectProject } = useProjectStore()

  useEffect(() => {
    if (project && currentProject?.application.folder_name !== project) selectProject(project)
  }, [project, currentProject, selectProject])

  const [area, setArea] = useState<BusinessArea>(() => blankArea(aName))
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const { canEditObjectsOrLoading: canEdit } = useProjectPermissions()
  const [generating, setGenerating] = useState(false)
  const [aiOpen, setAiOpen] = useState(false)
  const [aiPrompt, setAiPrompt] = useState('')
  const [view, setView] = useState<ViewTab>('tables')
  const [importOpen, setImportOpen] = useState(false)
  const [schemaImportOpen, setSchemaImportOpen] = useState(false)
  const [schemaList, setSchemaList] = useState<VisualSchemaSummary[]>([])
  const [appList, setAppList] = useState<ProjectApplication[]>([])
  // The attached schema's connection — with a schema, the area's data source
  // IS the schema's and cannot be edited here.
  const [schemaConn, setSchemaConn] = useState('')
  const varTokens = useSqlVariableTokens(projectName)
  const funcRoles = useFunctionalRoles(projectName)

  // Schema designer opened over this page (create-new / edit-attached), and
  // the small name+description dialog that precedes a create.
  const [schemaEditor, setSchemaEditor] = useState<string | null>(null)
  const [newSchemaOpen, setNewSchemaOpen] = useState(false)

  const refreshSchemas = useCallback(async () => {
    if (!projectName) return
    try {
      const res = await listVisualSchemas(projectName)
      setSchemaList(res.schemas || [])
    } catch {
      // Non-fatal: the picker keeps whatever it already listed.
    }
  }, [projectName])

  useEffect(() => {
    if (!projectName) return
    void refreshSchemas()
    listApplications(projectName)
      .then((res) => setAppList(res.applications || []))
      .catch(() => setAppList([]))
  }, [projectName, refreshSchemas])

  useEffect(() => {
    const ref = (area.SCHEMA_NAME || '').trim()
    if (!projectName || !ref) { setSchemaConn(''); return }
    let cancelled = false
    loadVisualSchema(projectName, ref)
      .then((res) => {
        if (cancelled) return
        const raw = res?.content as Record<string, unknown> | null
        const inner = (raw?.SCHEMA as Record<string, unknown>) || raw
        setSchemaConn(String(inner?.CONNECTION_ID || '').trim())
      })
      .catch(() => { if (!cancelled) setSchemaConn('') })
    return () => { cancelled = true }
  }, [projectName, area.SCHEMA_NAME])

  const [appModalOpen, setAppModalOpen] = useState(false)
  const saveNewApplication = useCallback(async (name: string, description: string) => {
    const trimmed = (name || '').trim()
    if (!trimmed) return
    try {
      const res = await saveApplication(projectName, trimmed, (description || '').trim())
      setAppList(res.applications || [])
      update((a) => { a.APPLICATION = trimmed })
      setAppModalOpen(false)
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(`Could not add application: ${detail || (err instanceof Error ? err.message : String(err))}`)
    }
    // update() is a stable useCallback([]) declared below — safe to call at
    // runtime; kept out of deps to avoid a render-time TDZ reference.
  }, [projectName])

  // Create an empty Visual Schema from inside the area, attach it, and open
  // the SAME schema designer the M object type uses so it can be filled in
  // right here. The area's own connection seeds the schema's (the schema then
  // owns the data source — see the picker below).
  const createSchemaFromArea = useCallback(async (name: string, description: string) => {
    const schemaName = slugifyName(name)
    if (!projectName) return
    if (schemaList.some((s) => s.schema_name === schemaName)) {
      toast.error(`A schema named "${schemaName}" already exists — pick it from the list instead`)
      return
    }
    try {
      await saveVisualSchema(
        projectName, schemaName,
        blankSchemaFile(schemaName, description, area.CONNECTION_ID || ''),
      )
      await refreshSchemas()
      update((a) => {
        a.SCHEMA_NAME = schemaName
        // With a schema attached the connection always comes from the schema.
        a.CONNECTION_ID = ''
      })
      setNewSchemaOpen(false)
      setSchemaEditor(schemaName)
      toast.success(`Schema "${schemaName}" created and attached — import its tables and joins`)
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(`Could not create the schema: ${detail || (err instanceof Error ? err.message : String(err))}`)
    }
    // update() is a stable useCallback([]) declared below — see above.
  }, [projectName, schemaList, area.CONNECTION_ID, refreshSchemas])
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null)
  const isDirty = savedSnapshot !== null && JSON.stringify(area) !== savedSnapshot
  const history = useDesignHistory(area, setArea, { disabled: !canEdit })
  const { reset: resetHistory } = history

  const agentRefresh = useAgentRefresh(projectName, isDirty)

  useEffect(() => {
    let cancelled = false
    if (!projectName || !aName) return
    setLoading(true)
    loadBusinessArea(projectName, aName)
      .then((data) => {
        if (cancelled) return
        const next = data && data.content ? sanitizeArea(data.content, aName) : blankArea(aName)
        setArea(next)
        setSavedSnapshot(JSON.stringify(next))
        // A (re)load replaces the area wholesale — stale undo entries would
        // restore a different/older business area.
        resetHistory()
      })
      .catch((err) => {
        console.error('Failed to load business area:', err)
        toast.error('Failed to load business area')
        const fallback = blankArea(aName)
        setArea(fallback); setSavedSnapshot(JSON.stringify(fallback))
        resetHistory()
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [projectName, aName, agentRefresh, resetHistory])

  const update = useCallback((mutator: (a: BusinessArea) => void) => {
    setArea((prev) => { const next = JSON.parse(JSON.stringify(prev)) as BusinessArea; mutator(next); return next })
  }, [])

  const doSave = useCallback(async (): Promise<boolean> => {
    if (!projectName || !aName) return false
    setSaving(true)
    try {
      const payload = { BUSINESS_AREA: { ...area, AREA_NAME: aName, AREA_TYPE: 'A' } }
      await saveBusinessArea(projectName, aName, payload)
      setSavedSnapshot(JSON.stringify(area))
      toast.success('Business area saved')
      // The list card's table count is derived server-side — tell the shell.
      onSaved?.()
      return true
    } catch (err) {
      console.error('Save failed', err)
      toast.error('Failed to save business area')
      return false
    } finally { setSaving(false) }
  }, [projectName, aName, area, onSaved])

  // Embedded-mode plumbing (dirty reporting + save registration).
  useEffect(() => { onDirtyChange?.(isDirty) }, [isDirty, onDirtyChange])
  const saveRef = useRef(doSave); saveRef.current = doSave
  useEffect(() => { registerSave?.(() => saveRef.current()) }, [registerSave])

  const generate = useCallback(async () => {
    if (!aiPrompt.trim()) return
    setGenerating(true)
    try {
      const res = await generateBusinessArea({
        project: projectName, area_name: aName, description: aiPrompt.trim(),
        // Ground on the area's bound external connection (if any) so the AI
        // draws from that source's real tables instead of guessing.
        connection_id: (area.CONNECTION_ID || '').trim() || undefined,
      })
      if (res.error) { toast.error(res.error); return }
      if (res.content) { setArea(sanitizeArea(res.content, aName)) }
      toast.success('Business area generated')
      setAiOpen(false); setAiPrompt('')
    } catch (err) {
      console.error('Generate failed', err)
      toast.error('Generation failed')
    } finally { setGenerating(false) }
  }, [aiPrompt, projectName, aName, area.CONNECTION_ID])

  if (loading) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: embedded ? '100%' : '60vh', color: 'var(--color-text-secondary)' }}>
      <Loader2 className="animate-spin" size={24} /> <span style={{ marginLeft: 8 }}>Loading…</span>
    </div>
  }

  const readOnly = !canEdit

  return (
    // Editor frame (spec §6.2: container padding 20/28/60, max-width 1180 over
    // the --color-bg page). The WHOLE page scrolls (header + tabs + body
    // together) inside the shell's overflow:auto wrapper — the root just flows
    // to its content height rather than owning an internal scroll pane.
    <div style={{
      display: 'flex', flexDirection: 'column',
      color: 'var(--color-text)', background: 'var(--color-bg)',
    }}>
      {/* ── Header rows (scroll with the page) ── */}
      <div style={{ flex: 'none', padding: '18px 28px 0' }}>
      <div style={{ maxWidth: 1180 }}>
      {/* Top row: back navigation (standalone only) + save hint */}
      {!embedded && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
          <button onClick={() => navigate(-1)} style={{ ...btn, border: '1px solid var(--color-border)', padding: '7px 13px' }}>
            <ArrowLeft size={14} /> Business Areas
          </button>
          <span style={{ fontSize: 15, color: 'var(--color-text-faint)' }}>
            Use the editor&apos;s <b style={{ fontWeight: 600, color: 'var(--color-text-secondary)' }}>Save</b> button to persist your changes.
          </span>
        </div>
      )}

      {/* Title row (spec §6.2.1: A chip · UPPERCASE name · badge · dirty · actions) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
        <span aria-hidden style={{
          width: 30, height: 30, borderRadius: 8, background: '#f0883e', color: '#fff',
          fontWeight: 700, fontSize: 15, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          flex: 'none',
        }}>A</span>
        <h1 style={{ margin: 0, fontWeight: 700, fontSize: 22, letterSpacing: '-0.3px', textTransform: 'uppercase' }}>{aName}</h1>
        <span style={{ fontSize: 13, fontWeight: 600, padding: '3px 10px', borderRadius: 8, background: 'var(--color-surface-2)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}>
          Business Area
        </span>
        {isDirty && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--color-warning, #d97706)' }}>
            <span aria-hidden style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--color-warning, #d97706)' }} />
            unsaved
          </span>
        )}
        {readOnly && <ReadOnlyBadge />}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {!readOnly && <UndoRedoButtons history={history} />}
          {!embedded && (
            <button onClick={() => navigate(`/designers/assistant/${encodeURIComponent(projectName)}`)} style={btn} title="Configure which AI profile, capabilities and Business Areas the end-user Assistant uses">
              <Bot size={15} /> Assistant settings
            </button>
          )}
          {!readOnly && (
            <button onClick={() => setAiOpen(true)} style={btnAccentText}>
              <Sparkles size={15} /> Generate with AI
            </button>
          )}
          {!readOnly && (
            <button onClick={doSave} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.7 : 1 }}>
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
            </button>
          )}
        </div>
      </div>

      {/* Instruction to AI — guidance fed to the Assistant AND the Ask-AI
          visual generator whenever they turn a question into SQL. */}
      <label style={{ ...fieldLabel, marginBottom: 18 }}>
        <span>Instruction to AI <span style={labelNote}>— rules the Assistant follows for every question in this area (units, fiscal year, what to exclude/prefer)</span></span>
        <textarea rows={2} style={{ ...inp, resize: 'vertical', minHeight: 90 }} value={area.AI_INSTRUCTIONS || ''} disabled={readOnly}
          placeholder="e.g. Amounts are INR. 'this year' = fiscal year from Apr 1. Exclude CANCELLED invoices. Prefer POSTED status."
          onChange={(e) => update((a) => { a.AI_INSTRUCTIONS = e.target.value })} />
      </label>

      {/* Meta row 1 */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
        <label style={{ ...fieldLabel, flex: 1, minWidth: 240 }}>
          Description
          <input style={inp} value={area.DESCRIPTION} disabled={readOnly}
            onChange={(e) => update((a) => { a.DESCRIPTION = e.target.value })} />
        </label>
        <label style={{ ...fieldLabel, width: 300 }}>
          Visibility
          <select style={inp} value={area.VISIBILITY} disabled={readOnly}
            onChange={(e) => update((a) => { a.VISIBILITY = e.target.value })}>
            <option value="published">Published (served to the Assistant)</option>
            <option value="draft">Draft (hidden from the Assistant)</option>
          </select>
        </label>
      </div>

      {/* Meta row 2: which database the area queries + optional shared
          dataset (a Visual Schema whose tables/joins visuals will use). */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 18, flexWrap: 'wrap', alignItems: 'end' }}>
        <div style={{ ...fieldLabel, flex: 1, minWidth: 260 }}>
          {(area.SCHEMA_NAME || '').trim()
            ? <span>Data source <span style={labelNote}>— from schema {area.SCHEMA_NAME}</span></span>
            : 'Data source'}
          <ConnectionPicker
            project={projectName}
            value={(area.SCHEMA_NAME || '').trim() ? schemaConn : (area.CONNECTION_ID || '')}
            disabled={readOnly || !!(area.SCHEMA_NAME || '').trim()}
            defaultLabel={(area.SCHEMA_NAME || '').trim()
              ? '(Project database — set on the schema)'
              : '(Project database)'}
            onChange={(cid) => update((a) => { a.CONNECTION_ID = cid })}
          />
        </div>
        <div style={{ ...fieldLabel, minWidth: 300 }}>
          <span>Dataset schema <span style={labelNote}>(optional)</span></span>
          <div style={{ display: 'flex', gap: 6 }}>
            <select style={{ ...inp, flex: 1 }} value={area.SCHEMA_NAME || ''} disabled={readOnly}
              onChange={(e) => update((a) => {
                a.SCHEMA_NAME = e.target.value
                // With a schema attached the connection always comes from the
                // schema — the area's own binding is cleared and locked.
                if (e.target.value.trim()) a.CONNECTION_ID = ''
              })}>
              <option value="">(This area&apos;s own tables &amp; joins)</option>
              {schemaList.map((s) => (
                <option key={s.schema_name} value={s.schema_name}>
                  {s.schema_name}{s.table_count ? ` (${s.table_count} tables)` : ''}
                </option>
              ))}
            </select>
            {(area.SCHEMA_NAME || '').trim() && (
              <button type="button" style={{
                ...btn, width: 40, justifyContent: 'center', padding: 6,
                border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)',
              }}
                title={`Open "${area.SCHEMA_NAME}" in the schema designer`}
                onClick={() => setSchemaEditor(area.SCHEMA_NAME || null)}>
                <Table2 size={14} />
              </button>
            )}
            {!readOnly && (
              <button type="button" style={{
                ...btn, width: 40, justifyContent: 'center', padding: 6,
                border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)',
              }}
                title="Create a new schema design and attach it to this area"
                onClick={() => setNewSchemaOpen(true)}>
                <Plus size={14} />
              </button>
            )}
          </div>
        </div>
        <div style={{ ...fieldLabel, minWidth: 220 }}>
          Application
          <div style={{ display: 'flex', gap: 6 }}>
            <select style={{ ...inp, flex: 1 }} value={area.APPLICATION || 'Default'} disabled={readOnly}
              title="Which external application generates this data. Default = this project's own application."
              onChange={(e) => update((a) => { a.APPLICATION = e.target.value })}>
              <option value="Default">Default (this project)</option>
              {appList.map((ap) => (
                <option key={ap.name} value={ap.name}>
                  {ap.name}{ap.description ? ` — ${ap.description}` : ''}
                </option>
              ))}
            </select>
            {!readOnly && (
              <button type="button" style={{
                ...btn, width: 40, justifyContent: 'center', padding: 6,
                border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)',
              }}
                title="Add an application to the project catalog"
                onClick={() => setAppModalOpen(true)}>
                <Plus size={14} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs (underline style, spec §0.3 / §6.2.5) */}
      <div style={{ display: 'flex', gap: 22, borderBottom: '1px solid var(--color-border)' }}>
        {([
          ['tables', 'Tables', Table2],
          ['joins', 'Joins', FileInput],
          ['security', 'Security', Shield],
          ['assistant', 'Assistant', MessageSquare],
          ['preview', 'Preview', Eye],
        ] as [ViewTab, string, typeof Table2][]).map(([id, label, Icon]) => (
          <button key={id} onClick={() => setView(id)} style={{
            padding: '10px 3px', border: 'none', background: 'none', cursor: 'pointer',
            fontSize: 15, fontWeight: view === id ? 600 : 400, whiteSpace: 'nowrap',
            color: view === id ? 'var(--color-text)' : 'var(--color-text-secondary)',
            borderBottom: view === id ? '2px solid var(--color-accent)' : '2px solid transparent',
            marginBottom: -1,
            display: 'flex', alignItems: 'center', gap: 6,
          }}><Icon size={14} /> {label}</button>
        ))}
      </div>
      </div>
      </div>

      {/* ── Body (flows with the page — no internal scroll pane) ── */}
      <div style={{ padding: '20px 28px 60px' }}>
      <div style={{ maxWidth: 1180 }}>
      {view === 'tables' && (
        <TablesTab area={area} update={update} readOnly={readOnly} varTokens={varTokens}
          onImport={() => setImportOpen(true)} onImportSchema={() => setSchemaImportOpen(true)}
          project={projectName} sqlTarget={sqlTargetOf((area.SCHEMA_NAME || '').trim() ? schemaConn : (area.CONNECTION_ID || ''))} />
      )}
      {view === 'joins' && (
        <JoinsTab
          area={area} update={update} readOnly={readOnly}
          onAddTable={() => setImportOpen(true)}
          project={projectName}
          connectionId={(area.SCHEMA_NAME || '').trim() ? schemaConn : (area.CONNECTION_ID || '')}
        />
      )}
      {view === 'security' && <SecurityTab area={area} update={update} readOnly={readOnly}
        varTokens={varTokens} funcRoles={funcRoles}
        project={projectName} sqlTarget={sqlTargetOf((area.SCHEMA_NAME || '').trim() ? schemaConn : (area.CONNECTION_ID || ''))} />}
      {view === 'assistant' && <AssistantTab area={area} update={update} readOnly={readOnly} />}
      {view === 'preview' && <PreviewTab project={projectName} areaName={aName} dirty={isDirty} />}
      </div>
      </div>

      {importOpen && <ImportTableModal project={projectName} onClose={() => setImportOpen(false)}
        // Import from the area's bound external connection (its own CONNECTION_ID,
        // or the attached schema's) when set — otherwise the project database.
        connectionId={(area.SCHEMA_NAME || '').trim() ? schemaConn : (area.CONNECTION_ID || '')}
        existing={new Set(area.TABLES.map((t) => t.TABLE_NAME))}
        onImport={(t, cols) => update((a) => {
          const upper = t.toUpperCase()
          const ex = a.TABLES.find((x) => x.TABLE_NAME === upper)
          if (ex) { const have = new Set(ex.COLUMNS.map((c) => c.DBNAME)); for (const c of cols) if (!have.has(c.DBNAME)) ex.COLUMNS.push(c) }
          else a.TABLES.push({ TABLE_NAME: upper, TABLE_DISPLAY_NAME: prettify(upper), ALIAS: upper.split('_').map((p) => p[0] || '').join('').slice(0, 4), DESCRIPTION: '', COLUMNS: cols })
        })} />}

      {schemaImportOpen && <ImportSchemaModal project={projectName} onClose={() => setSchemaImportOpen(false)}
        onImport={(tables, joins) => update((a) => {
          const have = new Set(a.TABLES.map((t) => t.TABLE_NAME))
          for (const t of tables) if (!have.has(t.TABLE_NAME)) a.TABLES.push(t)
          const jids = new Set(a.JOINS.map((j) => j.JOIN_CONDITION))
          for (const j of joins) if (!jids.has(j.JOIN_CONDITION)) a.JOINS.push(j)
        })} />}

      {appModalOpen && (
        <NewApplicationModal onClose={() => setAppModalOpen(false)} onSave={saveNewApplication} />
      )}

      {newSchemaOpen && (
        <NewSchemaModal
          existing={schemaList.map((s) => s.schema_name)}
          onClose={() => setNewSchemaOpen(false)}
          onCreate={createSchemaFromArea}
        />
      )}

      {schemaEditor && (
        <SchemaDesignerModal
          project={projectName}
          schemaName={schemaEditor}
          onClose={() => { setSchemaEditor(null); void refreshSchemas() }}
          onSaved={() => { void refreshSchemas() }}
        />
      )}

      {aiOpen && (
        <Modal title="Generate Business Area with AI" onClose={() => setAiOpen(false)}>
          <p style={{ fontSize: 15, lineHeight: 1.55, color: 'var(--color-text-secondary)', marginTop: 0 }}>
            Describe the dataset and the kinds of questions the Assistant should answer. It will ground every table/column against the project database.
          </p>
          <textarea autoFocus value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} rows={5}
            placeholder="e.g. Sales analytics over invoices, lines, customers and items. Users ask about revenue by month, top customers, out-of-stock products. Hide cost columns from site-visitor."
            style={{ ...inp, width: '100%', resize: 'vertical' }} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
            <button onClick={() => setAiOpen(false)} style={btn}>Cancel</button>
            <button onClick={generate} disabled={generating || !aiPrompt.trim()}
              style={{ ...btnPrimary, opacity: generating || !aiPrompt.trim() ? 0.6 : 1 }}>
              {generating ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Generate
            </button>
          </div>
        </Modal>
      )}
    </div>
  )
}

// ── Tables tab ──────────────────────────────────────────────────────
function TablesTab({ area, update, readOnly, varTokens, onImport, onImportSchema, project, sqlTarget }: {
  area: BusinessArea; update: (m: (a: BusinessArea) => void) => void; readOnly: boolean
  varTokens: VariableToken[]
  onImport: () => void; onImportSchema: () => void
  project?: string; sqlTarget?: string
}) {
  // Columns are collapsed by default — expand a table to edit them.
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const toggleExpand = (key: string) => setExpanded((prev) => {
    const next = new Set(prev)
    if (next.has(key)) next.delete(key); else next.add(key)
    return next
  })
  return (
    <div>
      {!readOnly && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          <button onClick={onImport} style={btn}><Plus size={14} /> Import table</button>
          <button onClick={onImportSchema} style={btn}><Download size={14} /> Import from Visual Schema</button>
        </div>
      )}
      {area.TABLES.length === 0 && <Empty text="No tables yet. Import a table or generate with AI." />}
      {area.TABLES.map((t, ti) => {
        const isMain = !!t.TABLE_NAME && (area.MAIN_TABLE || '') === t.TABLE_NAME
        const tkey = t.TABLE_NAME || `__t${ti}`
        const isOpen = expanded.has(tkey)
        return (
        <div key={ti} style={{ ...card, marginBottom: 14 }}>
          {/* Header strip (spec §6.2.6): ▸ · star · ▦ chip · name · alias · description · 🗑 */}
          <div style={{ ...strip, display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', flexWrap: 'wrap' }}>
            <button type="button" onClick={() => toggleExpand(tkey)} title={isOpen ? 'Collapse columns' : 'Expand columns'}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', color: 'var(--color-text-secondary)' }}>
              {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
            <button type="button" disabled={readOnly}
              onClick={() => update((a) => { a.MAIN_TABLE = a.MAIN_TABLE === t.TABLE_NAME ? '' : t.TABLE_NAME })}
              style={{ background: 'none', border: 'none', cursor: readOnly ? 'default' : 'pointer', padding: 2, display: 'flex' }}
              title={isMain
                ? 'Main (fact) table — queries start FROM it and join outward. Click to unset.'
                : 'Mark as the main (fact) table — queries start FROM it and join outward'}>
              <Star size={16} fill={isMain ? 'var(--color-warning, #f59e0b)' : 'none'}
                style={{ color: isMain ? 'var(--color-warning, #f59e0b)' : 'var(--color-text-faint)' }} />
            </button>
            <span aria-hidden style={{
              width: 18, height: 18, borderRadius: 4, background: 'var(--color-accent-weak)',
              color: 'var(--color-accent)', display: 'inline-flex', alignItems: 'center',
              justifyContent: 'center', flex: 'none',
            }}><Table2 size={11} /></span>
            <strong style={{ fontSize: 14, fontWeight: 700, fontFamily: MONO }}>{t.TABLE_NAME}</strong>
            {isMain && <span style={{ fontSize: 13, fontWeight: 600, padding: '1px 8px', borderRadius: 999, background: 'var(--color-warning-weak, rgba(245,158,11,0.15))', color: 'var(--color-warning, #f59e0b)' }}>main</span>}
            <input style={{ ...monoInp, width: 56, textAlign: 'center', padding: '7px 6px' }} placeholder="alias" value={t.ALIAS || ''} disabled={readOnly}
              onChange={(e) => update((a) => { a.TABLES[ti].ALIAS = e.target.value })} />
            <input style={{ ...inp, flex: 1, minWidth: 180, fontSize: 14.5 }} placeholder="table description (helps the Assistant)"
              value={(t as Record<string, unknown>).DESCRIPTION as string || ''} disabled={readOnly}
              onChange={(e) => update((a) => { (a.TABLES[ti] as Record<string, unknown>).DESCRIPTION = e.target.value })} />
            {!readOnly && <button title="Remove table" onClick={() => update((a) => { a.TABLES.splice(ti, 1) })} style={{ ...dangerBtn, marginLeft: 'auto' }}><Trash2 size={14} /></button>}
          </div>
          {!isOpen && (
            <div style={{ padding: '9px 14px', fontSize: 14, color: 'var(--color-text-secondary)' }}>
              {t.COLUMNS.length} column{t.COLUMNS.length === 1 ? '' : 's'}{t.CONDITION ? ' · enforced filter set' : ''} — expand to edit
            </div>
          )}
          {isOpen && (<>
          {/* Enforced-filter strip */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '8px 14px', borderBottom: '1px solid var(--color-border)' }}>
            <input style={{ ...monoInp, flex: 1 }}
              placeholder={`Enforced filter — auto-applied whenever ${t.TABLE_NAME || 'this table'} is queried, e.g. STATUS = 'POSTED'`}
              title="SQL condition AND-ed into the WHERE automatically whenever this table participates in a query (in addition to row/column security)"
              value={t.CONDITION || ''} disabled={readOnly}
              onChange={(e) => update((a) => { a.TABLES[ti].CONDITION = e.target.value })} />
            {!readOnly && t.TABLE_NAME && (
              <SqlBuilderButton value={t.CONDITION || ''} mode="where" compact
                contextTables={[t.TABLE_NAME]} project={project} target={sqlTarget}
                title={`Enforced filter on ${t.TABLE_NAME}`}
                onChange={(v) => update((a) => { a.TABLES[ti].CONDITION = v })} />
            )}
            {!readOnly && (
              <VariableInsert tokens={varTokens} onInsert={(tok) => update((a) => {
                a.TABLES[ti].CONDITION = `${a.TABLES[ti].CONDITION || ''} ${tok}`.trimStart()
              })} />
            )}
          </div>
          {/* Column grid — horizontally scrollable, header cells on surface-2 */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 15, minWidth: 860 }}>
              <thead><tr style={{ textAlign: 'left', background: 'var(--color-surface-2)' }}>
                <th style={th}>Column</th><th style={th}>Display</th><th style={th}>Type</th><th style={th}>Agg</th>
                <th style={th}>Description (NL hint)</th><th style={{ ...th, textAlign: 'center' }}>Hide</th><th style={th}></th>
              </tr></thead>
              <tbody>
                {t.COLUMNS.map((c, ci) => (
                  <tr key={ci} style={{ borderTop: '1px solid var(--color-border)' }}>
                    <td style={td}><input style={{ ...monoInp, width: 150 }} value={c.DBNAME} disabled={readOnly} onChange={(e) => update((a) => { a.TABLES[ti].COLUMNS[ci].DBNAME = e.target.value.toUpperCase() })} /></td>
                    <td style={td}><input style={{ ...inp, width: 140 }} value={c.DISPLAY_NAME || ''} disabled={readOnly} onChange={(e) => update((a) => { a.TABLES[ti].COLUMNS[ci].DISPLAY_NAME = e.target.value })} /></td>
                    <td style={td}><select style={{ ...monoInp, width: 86 }} value={c.COLTYPE || 'CHAR'} disabled={readOnly} onChange={(e) => update((a) => { a.TABLES[ti].COLUMNS[ci].COLTYPE = e.target.value as SchemaTableColumn['COLTYPE'] })}>{COLTYPE_OPTIONS.map((o) => <option key={o}>{o}</option>)}</select></td>
                    <td style={td}><select style={{ ...inp, width: 82 }} value={c.DEFAULTFUNCTION || ''} disabled={readOnly} onChange={(e) => update((a) => { a.TABLES[ti].COLUMNS[ci].DEFAULTFUNCTION = e.target.value })}>{FUNCTION_OPTIONS.map((o) => <option key={o} value={o}>{o || '—'}</option>)}</select></td>
                    <td style={td}><input style={{ ...inp, width: '100%', minWidth: 200 }} placeholder="natural-language hint" value={(c.DESCRIPTION as string) || ''} disabled={readOnly} onChange={(e) => update((a) => { a.TABLES[ti].COLUMNS[ci].DESCRIPTION = e.target.value })} /></td>
                    <td style={{ ...td, textAlign: 'center' }}><input type="checkbox" checked={!!c.HIDDEN} disabled={readOnly} style={{ width: 18, height: 18, accentColor: 'var(--color-accent)' }} onChange={(e) => update((a) => { a.TABLES[ti].COLUMNS[ci].HIDDEN = e.target.checked })} /></td>
                    <td style={td}>{!readOnly && <button title="Remove column" onClick={() => update((a) => { a.TABLES[ti].COLUMNS.splice(ci, 1) })} style={{ ...dangerBtn, padding: 4 }}><X size={13} /></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!readOnly && <button onClick={() => update((a) => { a.TABLES[ti].COLUMNS.push(blankColumn()) })} style={{ ...linkBtn, margin: '6px 12px 10px' }}><Plus size={13} /> Add column</button>}
          </>)}
        </div>
        )
      })}
    </div>
  )
}

// ── Joins tab ───────────────────────────────────────────────────────
// Diagram | List | Instruction — the shared JoinsEditor. Instruction edits the
// area-level JOINS_DESCRIPTION and, when made authoritative, flips JOIN_MODE to
// 'instruction' so the Assistant writes the query (raw SQL) from that text.
function JoinsTab({ area, update, readOnly, onAddTable, project, connectionId }: {
  area: BusinessArea; update: (m: (a: BusinessArea) => void) => void; readOnly: boolean
  onAddTable: () => void
  project: string
  /** The area's effective source — its own connection, or the schema's when it
   *  is seeded from one. Lets the key pickers offer real source columns. */
  connectionId: string
}) {
  // Add ONE physical column to a table already in the area, so a join key
  // picked from the source is also listed on its table (the query builder
  // requires that).
  const addSourceColumn = (tableName: string, col: SchemaTableColumn) => {
    update((a) => {
      const t = a.TABLES.find((x) => x.TABLE_NAME === (tableName || '').toUpperCase())
      if (!t || !col.DBNAME || t.COLUMNS.some((c) => c.DBNAME === col.DBNAME)) return
      t.COLUMNS.push(col)
    })
    toast.success(`Added ${tableName}.${col.DBNAME} to the area`)
  }
  return (
    <JoinsEditor
      tables={area.TABLES}
      mainTable={area.MAIN_TABLE}
      joins={area.JOINS}
      canEdit={!readOnly}
      onJoinsChange={(next) => update((a) => { a.JOINS = next })}
      layout={area.JOIN_LAYOUT}
      onLayoutChange={(next) => update((a) => { a.JOIN_LAYOUT = next })}
      onAddTable={onAddTable}
      columnSource={readOnly ? undefined : {
        load: (t) => fetchTableColumns(project, connectionId, t),
        adopt: addSourceColumn,
      }}
      sqlProject={project} sqlTarget={sqlTargetOf(connectionId || '')}
      instruction={{
        value: area.JOINS_DESCRIPTION || '',
        onChange: (v) => update((a) => { a.JOINS_DESCRIPTION = v }),
        authoritative: area.JOIN_MODE === 'instruction',
        onAuthoritativeChange: (v) => update((a) => { a.JOIN_MODE = v ? 'instruction' : 'structured' }),
      }}
    />
  )
}

// ── Security tab ────────────────────────────────────────────────────
const MASK_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: '(classic ********)' },
  { value: 'partial', label: 'Partial (keep last 4)' },
  { value: 'replace', label: 'Replace with text' },
  { value: 'null', label: 'Null / zero' },
  { value: 'hash', label: 'One-way hash' },
  { value: 'range', label: 'Value ranges' },
  { value: 'custom', label: 'Custom SQL' },
  { value: 'disallow', label: 'Disallow (block query)' },
]

function SecurityTab({ area, update, readOnly, varTokens, funcRoles, project, sqlTarget }: {
  area: BusinessArea; update: (m: (a: BusinessArea) => void) => void; readOnly: boolean
  varTokens: VariableToken[]; funcRoles: string[]
  project?: string; sqlTarget?: string
}) {
  const rangesText = (p: { RANGES?: { min?: number | string; max?: number | string; display: string }[] }) =>
    (p.RANGES || []).map((r) => `${r.min ?? ''}..${r.max ?? ''}=${r.display}`).join('; ')
  const parseRanges = (text: string) =>
    text.split(';').map((part) => part.trim()).filter(Boolean).map((part) => {
      const [bounds, display = ''] = part.split('=')
      const [min = '', max = ''] = (bounds || '').split('..')
      return {
        min: min.trim() === '' ? undefined : Number(min),
        max: max.trim() === '' ? undefined : Number(max),
        display: display.trim(),
      }
    })

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <section>
        <h4 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 700 }}>Row security</h4>
        <p style={{ margin: '0 0 12px', fontSize: 14.5, lineHeight: 1.55, color: 'var(--color-text-faint)', maxWidth: 920 }}>
          Filter which rows a role may read. Conditions may use :current_user_id, :current_user_email, :current_date, :current_timestamp (bound to the signed-in user at run time) and the project&apos;s own :variable_name variables. Table &quot;ALL&quot; applies the condition to every table.
        </p>
        {!readOnly && <button onClick={() => update((a) => { a.ROW_SECURITY = [...(a.ROW_SECURITY || []), blankRowSec((a.ROW_SECURITY?.length || 0) + 1)] })} style={{ ...btn, marginBottom: 10 }}><Plus size={13} /> Add row policy</button>}
        {(area.ROW_SECURITY || []).map((p, i) => (
          <div key={i} style={{ ...card, borderRadius: 11, padding: 12, marginBottom: 8, overflow: 'visible' }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <input list={`ba-rls-tables-${i}`} style={{ ...monoInp, width: 160 }} placeholder="table (or ALL)" value={p.TABLE_NAME} disabled={readOnly} onChange={(e) => update((a) => { a.ROW_SECURITY![i].TABLE_NAME = e.target.value })} />
              <datalist id={`ba-rls-tables-${i}`}>
                <option value="ALL">every table in the query</option>
                {area.TABLES.map((t) => <option key={t.TABLE_NAME} value={t.TABLE_NAME} />)}
              </datalist>
              <input style={{ ...monoInp, flex: 1, minWidth: 220 }} placeholder="CONDITION e.g. CUST_ID = :current_user_id" value={p.CONDITION} disabled={readOnly} onChange={(e) => update((a) => { a.ROW_SECURITY![i].CONDITION = e.target.value })} />
              {!readOnly && (
                <SqlBuilderButton value={p.CONDITION || ''} mode="where" compact
                  contextTables={p.TABLE_NAME && p.TABLE_NAME !== 'ALL' ? [p.TABLE_NAME] : []}
                  project={project} target={sqlTarget} title="Row security condition"
                  onChange={(v) => update((a) => { a.ROW_SECURITY![i].CONDITION = v })} />
              )}
              {!readOnly && (
                <VariableInsert tokens={varTokens} onInsert={(t) => update((a) => {
                  a.ROW_SECURITY![i].CONDITION = `${a.ROW_SECURITY![i].CONDITION || ''} ${t}`.trimStart()
                })} />
              )}
              <input style={{ ...inp, width: 180 }} placeholder="roles (blank = everyone)" value={p.APPLIES_TO_ROLES.join(', ')} disabled={readOnly} onChange={(e) => update((a) => { a.ROW_SECURITY![i].APPLIES_TO_ROLES = e.target.value.split(',').map((r) => r.trim()).filter(Boolean) })} />
              {!readOnly && (
                <RoleAdder roles={funcRoles} current={p.APPLIES_TO_ROLES || []}
                  onAdd={(r) => update((a) => {
                    a.ROW_SECURITY![i].APPLIES_TO_ROLES = [...(a.ROW_SECURITY![i].APPLIES_TO_ROLES || []), r]
                  })} />
              )}
              {!readOnly && <button title="Remove policy" onClick={() => update((a) => { a.ROW_SECURITY!.splice(i, 1) })} style={dangerBtn}><X size={13} /></button>}
            </div>
            <details style={{ marginTop: 8 }}>
              <summary style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-accent)', cursor: 'pointer' }}>Advanced (column gate / inherit from parent)</summary>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
                <label style={{ display: 'grid', gap: 2, fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary)' }}>
                  Only when column exists
                  <input style={{ ...inp, width: 160 }} placeholder="e.g. TENANT_ID" value={p.WHEN_COLUMN_EXISTS || ''} disabled={readOnly} onChange={(e) => update((a) => { a.ROW_SECURITY![i].WHEN_COLUMN_EXISTS = e.target.value })} />
                </label>
                <label style={{ display: 'grid', gap: 2, fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary)' }}>
                  Inherit from parent table
                  <input style={{ ...inp, width: 160 }} placeholder="e.g. ORDERS" value={p.INHERIT_FROM || ''} disabled={readOnly} onChange={(e) => update((a) => { a.ROW_SECURITY![i].INHERIT_FROM = e.target.value })} />
                </label>
                {(p.INHERIT_FROM || '').trim() && (
                  <label style={{ flex: 1, minWidth: 220, display: 'grid', gap: 2, fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary)' }}>
                    Join to parent (blank = use the area&apos;s defined join)
                    <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input style={{ ...inp, flex: 1 }} placeholder="ORDER_ITEMS.ORDER_ID = ORDERS.ORDER_ID" value={p.JOIN_CONDITION || ''} disabled={readOnly} onChange={(e) => update((a) => { a.ROW_SECURITY![i].JOIN_CONDITION = e.target.value })} />
                      {!readOnly && (
                        <SqlBuilderButton value={p.JOIN_CONDITION || ''} mode="where" compact
                          contextTables={[p.TABLE_NAME, p.INHERIT_FROM || ''].filter((x) => x && x !== 'ALL')}
                          project={project} target={sqlTarget} title="Join to parent"
                          onChange={(v) => update((a) => { a.ROW_SECURITY![i].JOIN_CONDITION = v })} />
                      )}
                    </span>
                  </label>
                )}
              </div>
            </details>
          </div>
        ))}
      </section>
      <section>
        <h4 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 700 }}>Column security</h4>
        <p style={{ margin: '0 0 12px', fontSize: 14.5, lineHeight: 1.55, color: 'var(--color-text-faint)', maxWidth: 920 }}>
          Hide, mask or restrict a column for a role. Masked values are anonymized in the SQL itself; &quot;Disallow&quot; rejects any question that touches the column.
        </p>
        {!readOnly && <button onClick={() => update((a) => { a.COLUMN_SECURITY = [...(a.COLUMN_SECURITY || []), blankColSec((a.COLUMN_SECURITY?.length || 0) + 1)] })} style={{ ...btn, marginBottom: 10 }}><Plus size={13} /> Add column policy</button>}
        {(area.COLUMN_SECURITY || []).map((p, i) => (
          <div key={i} style={{ ...card, borderRadius: 11, padding: 12, marginBottom: 8, overflow: 'visible' }}>
            <datalist id={`ba-cls-cols-${i}`}>
              {(p.TABLE_NAME
                ? tableColumns(area.TABLES, p.TABLE_NAME)
                : area.TABLES.flatMap((t) => (t.COLUMNS || []).map((c) => c.DBNAME).filter(Boolean))
              ).map((c) => <option key={c} value={c} />)}
            </datalist>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <input list={`ba-cls-tables-${i}`} style={{ ...monoInp, width: 160 }} placeholder="table" value={p.TABLE_NAME} disabled={readOnly} onChange={(e) => update((a) => { a.COLUMN_SECURITY![i].TABLE_NAME = e.target.value })} />
              <datalist id={`ba-cls-tables-${i}`}>
                {area.TABLES.map((t) => t.TABLE_NAME && <option key={t.TABLE_NAME} value={t.TABLE_NAME} />)}
              </datalist>
              <input list={`ba-cls-cols-${i}`} style={{ ...monoInp, width: 160 }} placeholder="column" value={p.COLUMN_NAME} disabled={readOnly} onChange={(e) => update((a) => { a.COLUMN_SECURITY![i].COLUMN_NAME = e.target.value })} />
              <select style={{ ...inp, width: 110, fontWeight: 600, fontSize: 15 }} value={p.ACTION} disabled={readOnly} onChange={(e) => update((a) => { a.COLUMN_SECURITY![i].ACTION = e.target.value })}>{COL_SEC_ACTIONS.map((o) => <option key={o}>{o}</option>)}</select>
              {p.ACTION === 'MASK' && (
                <select style={{ ...inp, width: 180, fontSize: 15 }} value={p.MASK_TYPE || ''} disabled={readOnly}
                  onChange={(e) => update((a) => { a.COLUMN_SECURITY![i].MASK_TYPE = e.target.value as typeof p.MASK_TYPE })}>
                  {MASK_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              )}
              <input style={{ ...inp, width: 180 }} placeholder="roles (blank = everyone)" value={p.APPLIES_TO_ROLES.join(', ')} disabled={readOnly} onChange={(e) => update((a) => { a.COLUMN_SECURITY![i].APPLIES_TO_ROLES = e.target.value.split(',').map((r) => r.trim()).filter(Boolean) })} />
              {!readOnly && (
                <RoleAdder roles={funcRoles} current={p.APPLIES_TO_ROLES || []}
                  onAdd={(r) => update((a) => {
                    a.COLUMN_SECURITY![i].APPLIES_TO_ROLES = [...(a.COLUMN_SECURITY![i].APPLIES_TO_ROLES || []), r]
                  })} />
              )}
              {!readOnly && <button title="Remove policy" onClick={() => update((a) => { a.COLUMN_SECURITY!.splice(i, 1) })} style={dangerBtn}><X size={13} /></button>}
            </div>
            {p.ACTION === 'MASK' && (p.MASK_TYPE === 'partial' || p.MASK_TYPE === 'replace' || p.MASK_TYPE === 'custom' || p.MASK_TYPE === 'range') && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
                {p.MASK_TYPE === 'partial' && (
                  <label style={{ flex: 1, minWidth: 240, display: 'grid', gap: 2, fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary)' }}>
                    Pattern (last 4 kept; use ****@domain.com for emails)
                    <input style={inp} placeholder="XXX-XX-####" value={p.PATTERN || ''} disabled={readOnly} onChange={(e) => update((a) => { a.COLUMN_SECURITY![i].PATTERN = e.target.value })} />
                  </label>
                )}
                {p.MASK_TYPE === 'replace' && (
                  <label style={{ flex: 1, minWidth: 240, display: 'grid', gap: 2, fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary)' }}>
                    Replacement text
                    <input style={inp} placeholder="CONFIDENTIAL" value={p.REPLACEMENT || ''} disabled={readOnly} onChange={(e) => update((a) => { a.COLUMN_SECURITY![i].REPLACEMENT = e.target.value })} />
                  </label>
                )}
                {p.MASK_TYPE === 'custom' && (
                  <label style={{ flex: 1, minWidth: 240, display: 'grid', gap: 2, fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary)' }}>
                    SQL expression ({'{col}'} = the column)
                    <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input style={{ ...monoInp, flex: 1 }} placeholder="EXTRACT(YEAR FROM {col})" value={p.SQL || ''} disabled={readOnly} onChange={(e) => update((a) => { a.COLUMN_SECURITY![i].SQL = e.target.value })} />
                      {!readOnly && (
                        <SqlBuilderButton value={p.SQL || ''} mode="expression" compact
                          contextTables={p.TABLE_NAME ? [p.TABLE_NAME] : []}
                          project={project} target={sqlTarget} title="Mask expression"
                          onChange={(v) => update((a) => { a.COLUMN_SECURITY![i].SQL = v })} />
                      )}
                    </span>
                  </label>
                )}
                {p.MASK_TYPE === 'range' && (
                  <label style={{ flex: 1, minWidth: 240, display: 'grid', gap: 2, fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary)' }}>
                    Ranges — min..max=Label; … (blank bound = open)
                    <input style={inp} placeholder="0..50000=< 50K; 50000..=50K+" value={rangesText(p)} disabled={readOnly} onChange={(e) => update((a) => { a.COLUMN_SECURITY![i].RANGES = parseRanges(e.target.value) })} />
                  </label>
                )}
              </div>
            )}
          </div>
        ))}
      </section>
    </div>
  )
}

// ── Assistant tab (AI instructions, examples, access, create targets) ─
function AssistantTab({ area, update, readOnly }: { area: BusinessArea; update: (m: (a: BusinessArea) => void) => void; readOnly: boolean }) {
  return (
    <div style={{ display: 'grid', gap: 22, maxWidth: 820 }}>
      <div style={{
        fontSize: 14.5, lineHeight: 1.55, color: 'var(--color-text-secondary)',
        padding: '10px 13px', border: '1.5px dashed var(--color-border-strong)', borderRadius: 10,
        background: 'var(--color-bg-secondary)',
      }}>
        The <b>Instruction to AI</b> is edited at the top of this page and the <b>Join guidance</b> on
        the Joins tab (they apply to the Assistant and the visual generator alike).
      </div>

      <div>
        <div style={{ ...fieldLabel, marginBottom: 8 }}><span>Sample questions <span style={labelNote}>— shown as starters; seed the chatbot&apos;s suggested prompts</span></span></div>
        {(area.EXAMPLE_QUESTIONS || []).map((q, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <input style={{ ...inp, flex: 1 }} value={q} disabled={readOnly} onChange={(e) => update((a) => { a.EXAMPLE_QUESTIONS![i] = e.target.value })} />
            {!readOnly && <button title="Remove question" onClick={() => update((a) => { a.EXAMPLE_QUESTIONS!.splice(i, 1) })} style={dangerBtn}><X size={13} /></button>}
          </div>
        ))}
        {!readOnly && <button onClick={() => update((a) => { a.EXAMPLE_QUESTIONS = [...(a.EXAMPLE_QUESTIONS || []), ''] })} style={linkBtn}><Plus size={13} /> Add question</button>}
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <label style={{ ...fieldLabel, flex: 1, minWidth: 220 }}>
          <span>Allowed roles <span style={labelNote}>— who may use this area (blank or * = any; use <code>site-visitor</code> for the public chatbot)</span></span>
          <input style={inp} placeholder="site-visitor, sales_rep" value={(area.ACCESS?.ALLOW_ROLES || []).join(', ')} disabled={readOnly}
            onChange={(e) => update((a) => { a.ACCESS = a.ACCESS || { ALLOW_ROLES: [], DENY_ROLES: [] }; a.ACCESS.ALLOW_ROLES = e.target.value.split(',').map((r) => r.trim()).filter(Boolean) })} />
        </label>
        <label style={{ ...fieldLabel, flex: 1, minWidth: 220 }}>
          Denied roles
          <input style={inp} placeholder="(none)" value={(area.ACCESS?.DENY_ROLES || []).join(', ')} disabled={readOnly}
            onChange={(e) => update((a) => { a.ACCESS = a.ACCESS || { ALLOW_ROLES: [], DENY_ROLES: [] }; a.ACCESS.DENY_ROLES = e.target.value.split(',').map((r) => r.trim()).filter(Boolean) })} />
        </label>
      </div>

      <div>
        <div style={{ ...fieldLabel, marginBottom: 8 }}>
          <span>Document sources <span style={labelNote}>— Document Repositories (obj type K) whose CONTENT the Assistant answers from (&quot;what does the policy say?&quot;). The repository&apos;s own rights, search flags and AI mode still decide what each reader may see — and its <b>question answering</b> switch must be ON (Document Designer → Repository)</span></span>
        </div>
        {(area.DOCUMENTS?.SOURCES || []).map((src, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <input style={{ ...monoInp, width: 200 }} placeholder="REPOSITORY_OBJ" value={src.REPO_OBJ} disabled={readOnly}
              onChange={(e) => update((a) => { a.DOCUMENTS!.SOURCES[i].REPO_OBJ = e.target.value.toUpperCase() })} />
            <input style={{ ...monoInp, width: 200 }} placeholder="doctypes (blank = all)" value={(src.DOCTYPES || []).join(', ')} disabled={readOnly}
              onChange={(e) => update((a) => { a.DOCUMENTS!.SOURCES[i].DOCTYPES = e.target.value.split(',').map((d) => d.trim().toUpperCase()).filter(Boolean) })} />
            <input style={{ ...inp, flex: 1, minWidth: 180 }} placeholder="description" value={src.DESCRIPTION || ''} disabled={readOnly}
              onChange={(e) => update((a) => { a.DOCUMENTS!.SOURCES[i].DESCRIPTION = e.target.value })} />
            {!readOnly && <button title="Remove source" onClick={() => update((a) => {
              a.DOCUMENTS!.SOURCES.splice(i, 1)
              if (!a.DOCUMENTS!.SOURCES.length && !a.DOCUMENTS!.AI_INSTRUCTIONS && !(a.DOCUMENTS!.EXAMPLE_QUESTIONS || []).length) a.DOCUMENTS = undefined
            })} style={dangerBtn}><X size={13} /></button>}
          </div>
        ))}
        {!readOnly && <button onClick={() => update((a) => {
          a.DOCUMENTS = a.DOCUMENTS || { SOURCES: [], AI_INSTRUCTIONS: '', EXAMPLE_QUESTIONS: [] }
          a.DOCUMENTS.SOURCES = [...a.DOCUMENTS.SOURCES, { REPO_OBJ: '', DOCTYPES: [], DESCRIPTION: '' }]
        })} style={linkBtn}><Plus size={13} /> Add document source</button>}
        {(area.DOCUMENTS?.SOURCES || []).length > 0 && (
          <label style={{ ...fieldLabel, marginTop: 8, display: 'block' }}>
            <span>Guidance for document answers <span style={labelNote}>— e.g. &quot;answer only from the current published revision; cite the document number&quot;</span></span>
            <textarea rows={2} style={{ ...inp, resize: 'vertical', width: '100%' }} value={area.DOCUMENTS?.AI_INSTRUCTIONS || ''} disabled={readOnly}
              onChange={(e) => update((a) => { a.DOCUMENTS!.AI_INSTRUCTIONS = e.target.value })} />
          </label>
        )}
      </div>

      <div>
        <div style={{ ...fieldLabel, marginBottom: 8 }}><span>Action targets <span style={labelNote}>— objects the Assistant may change data in (propose add / edit / delete or a custom action); each run only if the user is authorized for that object + action</span></span></div>
        {(area.CREATE_TARGETS || []).map((ct, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <input style={{ ...monoInp, width: 200 }} placeholder="OBJECT_NAME" value={ct.OBJ_NAME} disabled={readOnly} onChange={(e) => update((a) => { a.CREATE_TARGETS![i].OBJ_NAME = e.target.value })} />
            <input style={{ ...inp, flex: 1, minWidth: 200 }} placeholder="description" value={ct.DESCRIPTION || ''} disabled={readOnly} onChange={(e) => update((a) => { a.CREATE_TARGETS![i].DESCRIPTION = e.target.value })} />
            {!readOnly && <button title="Remove target" onClick={() => update((a) => { a.CREATE_TARGETS!.splice(i, 1) })} style={dangerBtn}><X size={13} /></button>}
          </div>
        ))}
        {!readOnly && <button onClick={() => update((a) => { a.CREATE_TARGETS = [...(a.CREATE_TARGETS || []), { OBJ_NAME: '', ACTIONS: ['add'], DESCRIPTION: '' }] })} style={linkBtn}><Plus size={13} /> Add create target</button>}
      </div>
    </div>
  )
}

// ── Preview tab (renders the prompt block the Assistant grounds on) ───
function PreviewTab({ project, areaName, dirty }: { project: string; areaName: string; dirty: boolean }) {
  const [roles, setRoles] = useState('')
  const [ctx, setCtx] = useState('')
  const [loading, setLoading] = useState(false)
  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await getBusinessAreaSqlContext(project, areaName, roles.split(',').map((r) => r.trim()).filter(Boolean))
      setCtx(res.sql_context || '')
    } catch { toast.error('Failed to load preview (save first)') } finally { setLoading(false) }
  }, [project, areaName, roles])
  return (
    <div>
      <p style={{ fontSize: 15, color: 'var(--color-text-secondary)', marginTop: 0, marginBottom: 12, maxWidth: 760 }}>
        The grounding block the Assistant sees for this area. {dirty && <strong>Save first to preview your latest edits.</strong>}
      </p>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, alignItems: 'center' }}>
        <input style={{ ...inp, width: 280 }} placeholder="roles to simulate (e.g. site-visitor)" value={roles} onChange={(e) => setRoles(e.target.value)} />
        <button onClick={load} disabled={loading} style={{ ...btn, opacity: loading ? 0.7 : 1 }}>{loading ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />} Preview</button>
      </div>
      {ctx && (
        // Grounding preview card (spec §6.2.10): header strip + mono body
        <div style={{ ...card, maxWidth: 980 }}>
          <div style={{ ...strip, display: 'flex', alignItems: 'center', gap: 7, padding: '9px 14px', fontSize: 14, fontWeight: 600 }}>
            <Sparkles size={13} style={{ color: 'var(--color-accent)', flex: 'none' }} /> Assistant grounding — {areaName}
          </div>
          <pre style={{ margin: 0, padding: 14, fontSize: 14.5, lineHeight: 1.6, fontFamily: MONO, whiteSpace: 'pre-wrap', overflowX: 'auto' }}>{ctx}</pre>
        </div>
      )}
    </div>
  )
}

// ── Import-table modal ──────────────────────────────────────────────
// Lists tables from the area's bound external connection (Oracle / Dremio /
// warehouse / any BI connection) when one is set, else from the project's own
// Database_Design. Without connection-awareness the picker only ever saw the
// local design JSONs, so an Oracle/Dremio-backed area showed no tables.
function ImportTableModal({ project, connectionId, onClose, onImport, existing }: {
  project: string; connectionId?: string; onClose: () => void; existing: Set<string>
  onImport: (table: string, cols: SchemaTableColumn[]) => void
}) {
  const conn = (connectionId || '').trim()
  const [tables, setTables] = useState<string[]>([])
  const [filter, setFilter] = useState('')
  const [busy, setBusy] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  useEffect(() => {
    let cancelled = false
    setLoading(true); setError('')
    // Connection-aware in ONE place (tableSource): the bound BI connection
    // when the area has one, else the project's Database_Design.
    listSourceTables(project, conn)
      .then((list) => { if (!cancelled) setTables(list) })
      .catch((err) => {
        if (cancelled) return
        setTables([])
        setError(err?.response?.data?.detail || 'Could not list tables for this connection.')
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [project, conn])
  const shown = useMemo(
    () => tables.filter((t) => t.toLowerCase().includes(filter.toLowerCase())),
    [tables, filter],
  )
  const importOne = async (t: string) => {
    setBusy(t)
    try {
      const cols = await fetchTableColumns(project, conn, t)
      onImport(t, cols)
      toast.success(`Imported ${t} (${cols.length} cols)`)
    } catch { toast.error(`Failed to import ${t}`) } finally { setBusy('') }
  }
  return (
    <Modal title={conn ? 'Import table from connection' : 'Import table'} onClose={onClose}>
      <input autoFocus style={{ ...inp, width: '100%', marginBottom: 10 }} placeholder="filter tables…" value={filter} onChange={(e) => setFilter(e.target.value)} />
      <div style={{ maxHeight: 320, overflowY: 'auto', display: 'grid', gap: 4 }}>
        {loading && <div style={{ padding: 20, textAlign: 'center', color: 'var(--color-text-secondary)' }}><Loader2 size={16} className="animate-spin" /> Loading tables…</div>}
        {!loading && error && <Empty text={error} />}
        {!loading && !error && shown.map((t) => (
          <button key={t} onClick={() => importOne(t)} disabled={!!busy} style={{ ...inp, cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Table2 size={13} /> {t} {existing.has(t.toUpperCase()) && <span style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>(added)</span>}
            {busy === t && <Loader2 size={13} className="animate-spin" style={{ marginLeft: 'auto' }} />}
          </button>
        ))}
        {!loading && !error && shown.length === 0 && <Empty text={conn ? 'No tables found on this connection.' : 'No tables match.'} />}
      </div>
    </Modal>
  )
}

// ── New-application modal ───────────────────────────────────────────
// Replaces the old window.prompt: an in-app modal that names the external
// application generating this area's data (with an optional description).
function NewApplicationModal({ onClose, onSave }: {
  onClose: () => void; onSave: (name: string, description: string) => Promise<void>
}) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [busy, setBusy] = useState(false)
  const submit = async () => {
    if (!name.trim() || busy) return
    setBusy(true)
    try { await onSave(name, description) } finally { setBusy(false) }
  }
  return (
    <Modal title="Add application" onClose={onClose}>
      <p style={{ fontSize: 15, lineHeight: 1.55, color: 'var(--color-text-secondary)', marginTop: 0 }}>
        The external application that generates this area&apos;s data (an ERP, CRM, warehouse feed, …). &quot;Default&quot; means this project&apos;s own application.
      </p>
      <div style={{ display: 'grid', gap: 12 }}>
        <label style={fieldLabel}>
          Application name
          <input autoFocus style={inp} value={name} placeholder="e.g. SAP ERP"
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void submit() }} />
        </label>
        <label style={fieldLabel}>
          <span>Description <span style={labelNote}>(optional)</span></span>
          <input style={inp} value={description} placeholder="what this application is"
            onChange={(e) => setDescription(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void submit() }} />
        </label>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
        <button onClick={onClose} style={btn}>Cancel</button>
        <button onClick={() => void submit()} disabled={!name.trim() || busy}
          style={{ ...btnPrimary, opacity: !name.trim() || busy ? 0.6 : 1 }}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Add application
        </button>
      </div>
    </Modal>
  )
}

// ── Import-from-Visual-Schema modal ─────────────────────────────────
function ImportSchemaModal({ project, onClose, onImport }: {
  project: string; onClose: () => void; onImport: (tables: SchemaTable[], joins: SchemaJoin[]) => void
}) {
  const [schemas, setSchemas] = useState<{ schema_name: string; table_count: number }[]>([])
  const [busy, setBusy] = useState('')
  useEffect(() => { listVisualSchemas(project).then((r) => setSchemas(r.schemas || [])).catch(() => setSchemas([])) }, [project])
  const importOne = async (name: string) => {
    setBusy(name)
    try {
      const r = await loadVisualSchema(project, name)
      const inner = ((r.content as Record<string, unknown>)?.SCHEMA as Record<string, unknown>) || {}
      onImport((inner.TABLES as SchemaTable[]) || [], (inner.JOINS as SchemaJoin[]) || [])
      toast.success(`Imported tables & joins from ${name}`)
      onClose()
    } catch { toast.error(`Failed to import ${name}`) } finally { setBusy('') }
  }
  return (
    <Modal title="Import from Visual Schema" onClose={onClose}>
      <p style={{ fontSize: 15, lineHeight: 1.55, color: 'var(--color-text-secondary)', marginTop: 0 }}>Copies the schema's tables and joins into this Business Area. Add the AI instructions and descriptions afterwards.</p>
      <div style={{ maxHeight: 320, overflowY: 'auto', display: 'grid', gap: 4 }}>
        {schemas.map((s) => (
          <button key={s.schema_name} onClick={() => importOne(s.schema_name)} disabled={!!busy} style={{ ...inp, cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Database size={13} /> {s.schema_name} <span style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>({s.table_count} tables)</span>
            {busy === s.schema_name && <Loader2 size={13} className="animate-spin" style={{ marginLeft: 'auto' }} />}
          </button>
        ))}
        {schemas.length === 0 && <Empty text="No visual schemas in this project." />}
      </div>
    </Modal>
  )
}

// ── New-schema modal (name + description) ───────────────────────────
// The area is the superset of its schema, so a schema can be born here: this
// collects the identity, then the full schema designer opens over the page.
function NewSchemaModal({ existing, onClose, onCreate }: {
  existing: string[]
  onClose: () => void
  onCreate: (name: string, description: string) => Promise<void>
}) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [busy, setBusy] = useState(false)
  const slug = slugifyName(name)
  const clashes = !!name.trim() && existing.includes(slug)
  const submit = async () => {
    if (!name.trim() || busy || clashes) return
    setBusy(true)
    try { await onCreate(name, description) } finally { setBusy(false) }
  }
  return (
    <Modal title="New schema design" onClose={onClose}>
      <p style={{ fontSize: 15, lineHeight: 1.55, color: 'var(--color-text-secondary)', marginTop: 0 }}>
        A schema design is the shared dataset — tables, joins, folders and security — this
        area and your visuals read from. It opens in the schema designer straight away.
      </p>
      <div style={{ display: 'grid', gap: 10 }}>
        <label style={fieldLabel}>
          Name
          <input autoFocus style={inp} value={name} placeholder="e.g. Pharma Sales Schema"
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void submit() }} />
          {!!name.trim() && (
            <span style={{ ...labelNote, marginTop: 4 }}>
              {clashes
                ? `"${slug}" already exists — pick it from the list or choose another name`
                : `saved as ${slug}`}
            </span>
          )}
        </label>
        <label style={fieldLabel}>
          <span>Description <span style={labelNote}>(optional)</span></span>
          <input style={inp} value={description} placeholder="what this dataset covers"
            onChange={(e) => setDescription(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void submit() }} />
        </label>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
        <button onClick={onClose} style={btn}>Cancel</button>
        <button onClick={() => void submit()} disabled={!name.trim() || busy || clashes}
          style={{ ...btnPrimary, opacity: !name.trim() || busy || clashes ? 0.6 : 1 }}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Create &amp; open
        </button>
      </div>
    </Modal>
  )
}

// ── Schema designer, hosted over the Business Area ──────────────────
// The SAME SchemaDesignerPage the M object type opens (embedded mode: it keeps
// its own Save button and drops the back-to-workbench arrow), so there is one
// schema editor in the product, not two.
function SchemaDesignerModal({ project, schemaName, onClose, onSaved }: {
  project: string; schemaName: string; onClose: () => void; onSaved: () => void
}) {
  const [dirty, setDirty] = useState(false)
  const handleClose = () => {
    if (dirty && !window.confirm(
      `"${schemaName}" has unsaved changes. Close the schema designer and discard them?`)) return
    onClose()
  }
  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2vh 2vw',
      }}
    >
      <div
        style={{
          background: 'var(--color-bg)', border: '1px solid var(--color-border)',
          borderRadius: 14, width: '96vw', height: '94vh', overflow: 'hidden',
          display: 'flex', flexDirection: 'column', color: 'var(--color-text)',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
          borderBottom: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)',
        }}>
          <Database size={14} style={{ color: 'var(--color-text-secondary)' }} />
          <strong style={{ fontSize: 15 }}>Schema design</strong>
          <span style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>
            — attached to this business area{dirty ? ' · unsaved changes' : ''}
          </span>
          <button onClick={handleClose} title="Close" style={{ ...iconBtn, marginLeft: 'auto' }}>
            <X size={15} />
          </button>
        </div>
        <div style={{ flex: 1, minHeight: 0 }}>
          <SchemaDesignerPage
            embedded
            embedProject={project}
            embedObjName={schemaName}
            onDirtyChange={setDirty}
            onSaved={onSaved}
          />
        </div>
      </div>
    </div>
  )
}

// ── small shared UI ─────────────────────────────────────────────────
function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', borderRadius: 16, padding: 20, width: 'min(560px, 92vw)', color: 'var(--color-text)' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
          <strong style={{ fontSize: 17, letterSpacing: '-0.2px' }}>{title}</strong>
          <button onClick={onClose} title="Close" style={{ ...iconBtn, marginLeft: 'auto' }}><X size={15} /></button>
        </div>
        {children}
      </div>
    </div>
  )
}
function Empty({ text }: { text: string }) {
  return (
    <div style={{
      padding: 30, textAlign: 'center', color: 'var(--color-text-secondary)', fontSize: 15,
      border: '1.5px dashed var(--color-border-strong)', borderRadius: 11, marginBottom: 12,
    }}>{text}</div>
  )
}
// Column-grid header cell (spec §6.2.6 eyebrow cells on surface-2)
const th: React.CSSProperties = {
  ...eyebrow, padding: '8px 10px',
}
const td: React.CSSProperties = { padding: '6px 8px' }
